import React, { useEffect, useState } from "react";
import { useStoreState } from "ezStore";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { saveAs } from "file-saver";

import { useLazyQuery } from "@apollo/client";
import { gql } from "__generated__/gql";
import { PageHeader, Card, Filter, Select, Button } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";

import { Box, Slider, styled, Switch } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import Planboard from "components/planboard2/Planboard";
import FleetSelect from "view/fleets/components/selects/FleetSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import { settingsProvider } from "components/utils/TableUtils";
import Helmet from "components/helmet/Helmet";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";
import ShipSelect from "view/ships/components/selects/ShipSelect";

import { getCallStatusList } from "view/calls/utils/CallUtils";
import { filterUnavailableByExceptions, generateDailyUnavailableBlocks } from "view/controltower/ControltowerUtils.tsx";

import { DataFormat, Language, hasPluginPermission, shortid } from "imports/Utils";
import { filterShipPlanning, createVoyageTask, createCharterItem, createEditableVoyageTask } from "./utils";
import "./styling/controltower.scss";


const ControlTowerBoard = () => {

	const [ callsForComments, setCallsForComments ] = useState([]);

	const { commentsProps } = useComments({
		calls: callsForComments,
	});

	return (
		<div className="controltower-board-page">
			<PageHeader>
				<PageHeader.Title>
					{Language("BOARD")}
					<Helmet title={intl.formatMessage({ id: "BOARD" })} />
				</PageHeader.Title>
			</PageHeader>

			<Board
				id="controltower-board"
				getCallsForComments={setCallsForComments}
				showExportButton />

			<CommentsWidget {...commentsProps} />
		</div>
	);
};

export const DisplayEnum = {
	PLANNED: "PLANNED_TIMES",
	ACTUAL: "ACTUAL_TIMES",
	BOTH: "BOTH",
};

export const CapacityEnum = {
	TEU: "TEU",
	MTON: "MTON",
};

const CharterEnum = {
	NONE: "NONE",
	PURCHASE: "PURCHASE",
};

const FilterWrapper = styled(Box)(({ theme }) => ({
	"& .cck-filter": {
		boxShadow: "0 1px 1px 0 rgba(0,0,0,0.14)",
		position: "relative",

		"& .MuiCollapse-root": {
			backgroundColor: theme.palette.background.paper,
			boxShadow: "0 1px 1px 0 rgba(0, 0 ,0, 0.14)",
			position: "absolute",
			width: "100%",
			zIndex: 101,
		},
	},
}));

export const Board = ({
	id,
	boardRef,
	planboardKey,
	renderEditableVoyages,
	showAllShips,
	excludeBargeTypes,
	onDeleteVoyage,
	onItemMove,
	onItemResize,
	enableShipLink,
	groupRenderer,
	itemRenderer,
	markers,
	selected,
	onItemSelect,
	onItemDeselect,
	showExportButton,
	onCanvasClick,
	getCallsForComments,
}) => {

	const { instance } = useCoastguard();

	const { filter } = settingsProvider();
	const filterFields = filter.settings(`${id}_filter`)?.fields || [];

	const hasDefaultValue = (itemId) => filterFields?.find(item => item.id === itemId)?.value;

	const session = useStoreState(state => state.session);
	const user = session.user;
	const useContainers = hasPluginPermission(session, "useContainers");

	const [ state, setState ] = useState({
		shipPlanning: [],
		groups: [],
		items: [],
		filter: {
			fleets: filterFields.length > 0 ? hasDefaultValue("fleets") : user?.properties?.fleets,
			ships: hasDefaultValue("ships") || undefined,
			display: hasDefaultValue("display") || DisplayEnum.PLANNED,
			capacity: hasDefaultValue("capacity") || CapacityEnum.TEU,
			charter: hasDefaultValue("charter") || CharterEnum.NONE,
			location: hasDefaultValue("location") || undefined,
			from: hasDefaultValue("from") || undefined,
			to: hasDefaultValue("to") || undefined,
			window: hasDefaultValue("window") || [ -14, 14 ],
			detailed: hasDefaultValue("detailed") || false,
			abbreviateText: hasDefaultValue("abbreviateText") || false,
		},
		showUnavailabilities: false,
	});

	const [ exportLoading, setExportLoading ] = useState(false);

	const [ getShipsPlanning, { data, loading, refetch } ] = useLazyQuery(SHIP_PLANNING_QUERY, {
		onCompleted: response => {

			const currentPlanning = filterShipPlanning(response.shipPlanning, state.filter.from, state.filter.to);

			setState(oldState => ({
				...oldState,
				shipPlanning: currentPlanning,
				groups: createGroups(currentPlanning),
				items: createItems(currentPlanning, response),
			}));

			if (getCallsForComments) {
				getCallsForComments(currentPlanning.flatMap(p => p.voyages).flatMap(v => [ ...v.calls ].map(c => ({ ...c, voyage: v }))));
			}
		},
	});

	const onToggleGroup = (groupId) => {
		setState(oldState => ({
			...oldState,
			groups: [ ...oldState.groups ].map((group => ({
				...group,
				open: group.parent === groupId ? !group.open : group.open,
			}))),
		}));
	};

	const onDownloadPlanning = () => {

		setExportLoading(true);

		const from = moment().startOf("day").add("days", state.filter.window?.[0] || -14).unix() * 1000;
		const to = moment().endOf("day").add("days", state.filter.window?.[1] || 14).unix() * 1000;
		const fleets = state.filter.fleets ? state.filter.fleets.map(fleet => `&fleet=${fleet.id}`).join("") : "";
		const url = `/shipreports/generate/planboard?from=${from}&to=${to}${fleets}`;

		instance.fetchBlob(url, {
			method: "GET",
			headers: {
				Accept: "application/vnd.ms-excel",
			},
		}).then(async (blob) => {
			saveAs(blob, "ShipReports.xlsx");
		}).finally(() => {
			setExportLoading(false);
		});
	};

	const ShipLink = ({ ship }) => {
		if (enableShipLink) {
			return (
				<span
					className="ship-link"
					onClick={(event) => {
						//don't trigger the onClick when there is a item dropped
						if (event.detail === 1) {
							window.open(`/view/voyages?shipId=${ship.id}&shipName=${ship.name}`, "_blank");
						}
					}}>
					{ship.name}
				</span>
			);
		} else {
			return ship.name;
		}
	};

	const createGroups = (planning) => {

		const groups = [];

		planning.forEach((item) => {
			if (state.filter.display === DisplayEnum.BOTH) {
				groups.push({
					id: item.ship.id,
					entity: item.ship,
					droppable: true,
					title: (
						<div className="sidebar-sub-toggle" onClick={() => onToggleGroup(item.ship.id)}>
							<KeyboardArrowDownIcon />
							<ShipLink ship={item.ship} />
						</div>
					),
				});

				groups.push({
					id: `${item.ship.id}_actual`,
					entity: item.ship,
					droppable: false,
					title: (
						<div className="sidebar-sub-group">
							{intl.formatMessage({ id: "ACTUAL_TIMES" })}
						</div>
					),
					parent: item.ship.id,
					open: true,
				});
			} else {
				groups.push({
					id: item.ship.id,
					entity: item.ship,
					droppable: true,
					title: <ShipLink ship={item.ship} />,
				});
			}
		});

		return groups;
	};

	const createItems = (planning, response) => {

		const items = [];
		const today = moment().startOf('day');

		planning.forEach((item) => {
			if (state.showUnavailabilities) {

				const exceptions = (item.ship.availabilityExceptions || [])
					.map(e => ({
						start: moment(e.startDate),
						end: moment(e.endDate),
					}))
					.filter(e => !e.end.isBefore(today));

				const dailyUnavailableBlocks = generateDailyUnavailableBlocks(item) || [];

				const unavailableBlocks = filterUnavailableByExceptions(dailyUnavailableBlocks, exceptions);

				exceptions.forEach(ex => {
					items.push({
						id: shortid.generate(),
						group: item.ship.id,
						start: ex.start,
						end: ex.end,
						className: "ship-availability-exception",
					});
				});

				items.push(...unavailableBlocks);
			}

			item.voyages.forEach((voyage) => {
				if (renderEditableVoyages) {
					items.push(createEditableVoyageTask(voyage, item.ship.id, onDeleteVoyage));
				} else {
					const isPlanned = state.filter.display === DisplayEnum.PLANNED;

					if ((state.filter.display === DisplayEnum.PLANNED) || (state.filter.display === DisplayEnum.ACTUAL)) {
						items.push(createVoyageTask(voyage, item.ship.id, state.filter, isPlanned, refetch));
					} else {
						items.push(createVoyageTask(voyage, item.ship.id, state.filter, true, refetch));
						items.push(createVoyageTask(voyage, item.ship.id, state.filter, false, refetch));
					}
				}
			});

			if (state.filter.charter === CharterEnum.PURCHASE) {
				const contracts = response.contracts ? response.contracts.content.filter(c => c.ships.map(s => s.id).includes(item.ship.id)) : [];

				contracts.forEach(contract => {
					if (contract?.charters?.length > 0) {
						contract.charters.forEach(charter => {
							items.push(createCharterItem(charter, item.ship.id));
						});
					}
				});
			}
		});

		return items;
	};

	usePropertyChangeEffect(() => {
		setState(oldState => ({
			...oldState,
			groups: createGroups(oldState.shipPlanning),
			items: createItems(oldState.shipPlanning, data),
		}));
	}, [ renderEditableVoyages, state.showUnavailabilities ]);

	useEffect(() => {
		getShipsPlanning({
			variables: {
				detailedMode: state.filter.detailed || false,
				filters: {
					shipIds: state.filter.ships ? state.filter.ships.map(ship => ship.id) : undefined,
					from: moment().startOf("day").add("days", state.filter.window?.[0] || -14).unix() * 1000,
					to: moment().endOf("day").add("days", state.filter.window?.[1] || 14).unix() * 1000,
					fleetIds: state.filter.fleets ? state.filter.fleets.map(item => item.id) : undefined,
					availability: true,
					capacity: true,
					cargos: true,
					allShips: showAllShips || false,
					excludeBargeTypes: excludeBargeTypes,
				},
				skipContracts: state.filter.charter !== CharterEnum.PURCHASE,
				contractFilters: {
					validFrom: moment().startOf("day").add("days", state.filter.window?.[0] || -14).unix() * 1000,
					validUntil: moment().endOf("day").add("days", state.filter.window?.[1] || 14).unix() * 1000,
				},
			},
		});
	}, [ state.filter ]);

	useEffect(() => {
		if (boardRef) {
			boardRef.current = {
				refetch,
			};
		}
	}, []);

	return (
		<div className="controltower-board" id="controltowerBoard">
			<Card disablePadding>
				<FilterWrapper>
					<Filter
						id={`${id}_filter`}
						datePickerPortalId='boardDatePicker'
						settingsProvider={filter}
						defaultOpen={false}
						fields={[ {
							id: "fleets",
							title: intl.formatMessage({ id: "FLEETS_SINGLE" }),
							render: (value, onChange) => <FleetSelect value={value} onChange={onChange} multiple />,
							defaultValue: state.filter.fleets,
						}, {

							id: "ships",
							title: intl.formatMessage({ id: "SHIPS" }),
							render: (value, onChange) => <ShipSelect value={value} onChange={onChange} multiple />,
							defaultValue: state.filter.ships,
						}, {
							id: "display",
							title: intl.formatMessage({ id: "ADMINISTRATION_DISPLAY" }),
							render: (value, onChange) => <EnumSelect enums={DisplayEnum} value={value} onChange={onChange} />,
							defaultValue: state.filter.display,
						}, {
							id: "capacity",
							title: intl.formatMessage({ id: "CAPACITY" }),
							render: (value, onChange) => <EnumSelect enums={CapacityEnum} value={value} onChange={onChange} />,
							defaultValue: state.filter.capacity,
							disabled: useContainers,
						}, {
							id: "charter",
							title: intl.formatMessage({ id: "CHARTER" }),
							render: (value, onChange) => <EnumSelect enums={CharterEnum} value={value} onChange={onChange} />,
							defaultValue: state.filter.charter,
						}, {
							id: "location",
							title: intl.formatMessage({ id: "LOCATION" }),
							render: (value, onChange) => <LocationSelect value={value} onChange={onChange} />,
							defaultValue: state.filter.location,
						}, {
							id: "from",
							title: intl.formatMessage({ id: "FROM" }),
							render: (value, onChange) => <LocationSelect value={value} onChange={onChange} />,
							defaultValue: state.filter.from,
						}, {
							id: "to",
							title: intl.formatMessage({ id: "TO" }),
							render: (value, onChange) => <LocationSelect value={value} onChange={onChange} />,
							defaultValue: state.filter.to,
						}, {
							id: "detailed",
							title: intl.formatMessage({ id: "DETAILED_MODE" }),
							filterType: "CHECKBOX",
							defaultValue: state.filter.detailed,
						}, {
							id: "abbreviateText",
							title: intl.formatMessage({ id: "TEXT_ABBREVIATE" }),
							filterType: "CHECKBOX",
							defaultValue: state.filter.abbreviateText,
						}, {
							id: "window",
							title: `${intl.formatMessage({ id: "PERIOD" })} (${intl.formatMessage({ id: "DAYS_FORWARD_AND_BACKWARD" })})`,
							render: (value, onChange) => (
								<div className="period-slider">
									<Slider
										aria-label="Always visible"
										valueLabelDisplay="on"
										value={value || [ -14, 14 ]}
										min={-90}
										max={90}
										size='small'
										marks={[ {
											value: -90,
											label: "-90",
										}, {
											value: 90,
											label: "90",
										} ]}
										onChange={(event, v) => onChange(v)} />
								</div>
							),
							defaultValue: state.filter.window,
						} ]}
						onSearch={newValues => {
							setState(oldState => ({
								...oldState,
								filter: {
									fleets: newValues.find(item => item.property === "fleets")?.value,
									ships: newValues.find(item => item.property === "ships")?.value,
									display: newValues.find(item => item.property === "display")?.value || DisplayEnum.PLANNED,
									capacity: newValues.find(item => item.property === "capacity")?.value || CapacityEnum.TEU,
									charter: newValues.find(item => item.property === "charter")?.value || CharterEnum.NONE,
									location: newValues.find(item => item.property === "location")?.value,
									from: newValues.find(item => item.property === "from")?.value,
									to: newValues.find(item => item.property === "to")?.value,
									window: newValues.find(item => item.property === "window")?.value,
									detailed: newValues.find(item => item.property === "detailed")?.value || false,
									abbreviateText: newValues.find(item => item.property === "abbreviateText")?.value || false,
								},
							}));
						}} />
				</FilterWrapper>

				<Planboard
					id={id}
					planboardKey={planboardKey}
					loading={loading}
					groups={state.groups.filter(group => typeof group.open === "boolean" ? group.open : true)}
					items={state.items}
					stackItems={true}
					onCanvasClick={onCanvasClick}
					onItemMove={onItemMove}
					onItemResize={onItemResize}
					groupRenderer={groupRenderer}
					itemRenderer={itemRenderer}
					markers={markers}
					selected={selected}
					onItemSelect={onItemSelect}
					onItemDeselect={onItemDeselect}
					onRefresh={() => refetch()}
					legend={<Legend />}
					optionalTools={(
						<div className="optional-tools">
							{showExportButton && (
								<Button
									onClick={() => onDownloadPlanning()}
									icon={<InsertDriveFileIcon fontSize="small" />}
									loading={exportLoading}
									variant="outlined"
									size="small">
									{intl.formatMessage({ id: "EXPORT" })}
								</Button>
							)}
							<Switch checked={state.showUnavailabilities}
								onChange={event => setState({ ...state, showUnavailabilities: event.target.checked })}
								size="small" />
							<React.Fragment>{intl.formatMessage({ id: "SHOW_SHIP_UNAVAILABLE" })}</React.Fragment>
						</div>
					)} />
			</Card>
		</div>
	);
};

const EnumSelect = ({ enums, value, onChange }) => {

	const options = Object.keys(enums).map((key) => ({
		id: enums[key],
		name: intl.formatMessage({ id: enums[key] }),
	}));

	return (
		<Select
			fetchOptions={options}
			onChange={option => onChange(option.id)}
			value={options.find(option => option.id === value)}
			clearable={false}
			disableSearch />
	);
};

const Legend = () => {

	const session = useStoreState(state => state.session);
	const statuses = session.statuses.CALL;

	return (
		<div className="controltower-board-legend">
			<div>
				<h3>{intl.formatMessage({ id: "STATUS" })}</h3>
				<ul>
					{statuses.map((status, index) => (
						<li key={index}>
							<label style={{ backgroundColor: status.color }} />
							{DataFormat.status(status, session.locale)}
						</li>
					))}
				</ul>
			</div>

			<div>
				<h3>{intl.formatMessage({ id: "CALL_CALCULATED" })}</h3>
				<ul>
					{[ ...getCallStatusList(session) ].map((item, index) => (
						<li key={index}>
							<label style={{ backgroundColor: item.color }} />
							{item.text}
						</li>
					))}

					<li className="no-actual-time">
						<label />
						{intl.formatMessage({ id: "NO_ACTUAL_TIMES" })}
					</li>
				</ul>
			</div>
		</div>
	);
};

export const SHIP_PLANNING_QUERY = gql(`#graphql
	query shipPlanning($filters: ShipPlanningFilters, $contractFilters: ContractSearch, $detailedMode: Boolean!, $skipContracts: Boolean!) {
		shipPlanning(filters: $filters, detailedMode: $detailedMode) {
			ship {
				id
				name
				operator {
					id
					name
				}
				owner {
					id
					name
				}
				availability {
					availableSunday
					availableWednesday
					availableTuesday
					availableThursday
					availableSaturday
					availableMonday
					availableFriday
					defaultAvailability
					availableDayNight
				}
				workingHours {
					sundayFrom
					sundayTo
					saturdayFrom
					saturdayTo
					fridayFrom
					fridayTo
					thursdayFrom
					thursdayTo
					wednesdayFrom
					wednesdayTo
					tuesdayFrom
					tuesdayTo
					mondayFrom
					mondayTo
				}
				availabilityExceptions {
					startDate
					endDate
				}
			}
			voyages {
				id
				voyageNumber
				start
				end
				purchaseCharters {
					id
					type
					charterNumber
				}
				ship {
					id
					name
				}
				from {
					id
					description
				}
				to {
					id
					description
				}
				calls {
					id
					location {
						id
						description
						code
						displayCode
					}
					actualStatusCode
					lastStatusChange {
						status {
							description
							color
						}
					}
					remainingTeuCapacity
					remainingWeightCapacity
					loadedTeuOnVoyageAfterCall
					loadedWeightOnVoyageAfterCall
					createTwinCall
					comments {
						...comment
					}
					weight @include(if: $detailedMode)
					volume @include(if: $detailedMode)
					cargos @include(if: $detailedMode) {
						cargo {
							id
							cargoNumber
							supplier {
								id
								name
							}
							product {
								id
								name
								description
							}
						}
					}
					...CallTimes
					numberOfLoadingContainers {
						...numberOfContainersFragment
					}
				}
			}
			availability {
				id
			}
		}
		contracts(pagination: {size: 999}, search: $contractFilters) @skip(if: $skipContracts) {
			content {
				id
				ships {
					id
					name
				}
				charters {
					id
					charterNumber
					... on PurchaseCharter {
						validFrom
						validUntil
					}
				}
			}
		}
	}
`);

export default ControlTowerBoard;
